<template>
  <div class="editable-tag-list-auto-complete">
    <combo-box
      ref="pickerRef"
      :title="title"
      :label="placeholder"
      :disabled="isDisabled"
      :options="unselectedOptions"
      :invalid-message="errorMessage"
      :auto-filter="true"
      @change="onChange"
      @input="onInput"
      @keydown.enter.prevent="onAddTag"
    >
    </combo-box>
    <cv-tag
      v-for="(item, index) in keywordTaglist"
      :key="item.value"
      :value="item.value"
      :label="item.label"
      filter
      kind="blue"
      :clear-aria-label="removeLabel"
      @remove="onRemove(index)"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";
import DOMUtils from "@/utils/dom.utils";

interface Option {
  name: string;
  label: string;
  value: string;
}

// Props
interface Props {
  tags?: string;
  message?: string;
  singleSelection?: boolean;
  currentSelection?: Option[];
  items?: unknown[];
  title?: string;
  placeholder?: string;
  characterLimit?: number;
}

const {
  tags = "",
  message = "",
  singleSelection = false,
  currentSelection = [],
  items = [],
  title = "",
  placeholder = "",
  characterLimit = 500,
} = defineProps<Props>();

// Emits
const emit = defineEmits<{
  remove: [data: unknown];
  change: [data: unknown];
  executed: [data: unknown];
}>();

// Composables
const { t } = useI18n();

// Template refs
const pickerRef = useTemplateRef<HTMLElement>("pickerRef");

// Reactive data
const removeLabel = ref(t("editable_tag_remove_label"));
const keywordTaglist = ref<Option[]>([]);
const errorMessage = ref<string>("");
const keyStrokeString = ref<string>("");
const dataOptions = ref([]);

// Computed
const isDisabled = computed(() => {
  if (singleSelection) {
    return selectedItems.value.length === 1;
  }
  return false;
});

const selectedItems = computed(() => {
  return currentSelection;
});

const unselectedOptions = computed(() => {
  return dataOptions.value.filter(
    (opt) => selectedItems.value && !selectedItems.value.some((item) => item.label === opt.label),
  );
});

const selectedOptions = computed(() => {
  const selectedOptions: Option[] = [];

  if (selectedItems.value) {
    selectedItems.value.forEach((item) => {
      selectedOptions.push({
        name: item.name,
        label: item.label,
        value: item.value,
      });
    });
  }
  return selectedOptions;
});

// Methods (maintaining original sequence)
const onAddTag = () => {
  if (keyStrokeString.value.trim() !== "" && validateInputValue(keyStrokeString.value)) {
    removeCustomOption();
    selectedOptions.value.push({
      name: keyStrokeString.value,
      label: keyStrokeString.value,
      value: keyStrokeString.value,
    });
    keywordTaglist.value = [...selectedOptions.value];
    clearFilter();
    emit("change", selectedOptions.value);
  }
};

const onRemove = (index: number) => {
  selectedOptions.value.splice(index, 1);
  keywordTaglist.value = [...selectedOptions.value];
  emit("change", selectedOptions.value);
};

const onChange = (value: string) => {
  if (value !== "" && validateInputValue(value)) {
    const option = dataOptions.value.find((opt) => opt.value === value);
    const optionIndex = dataOptions.value.findIndex((opt) => opt.value === value);

    if (option) {
      // This needs to be called before option.label is set to a new value, otherwise the condition will be false
      removeCustomOption();

      if (option.label.startsWith("Create") && optionIndex === 0) {
        option.label = value;
      }

      selectedOptions.value.push(option);
      keywordTaglist.value = [...selectedOptions.value];

      clearFilter();
      emit("change", selectedOptions.value);
    }
  } else if (value === "") {
    removeCustomOption();
  }
};

const handleSave = () => {
  if (errorMessage.value !== "") {
    emit("executed", { code: "error", data: [], errors: [] });
    return;
  }

  emit("executed", {
    code: "ok",
    data: keywordTaglist.value.map((item) => item.label).join(","),
    errors: [],
  });
};

const updateOptions = () => {
  dataOptions.value = items.map((item) => {
    return {
      name: item,
      label: item,
      value: item,
    };
  });
};

const updateKeywordTagList = () => {
  if (tags && tags !== "") {
    const keywords = tags.split(",");
    keywordTaglist.value = keywords.map((label) => {
      return {
        name: label,
        label: label,
        value: label,
      };
    });
  } else {
    keywordTaglist.value = [];
  }
};

const validateInputValue = (val: string): boolean => {
  const totalCharacterCount =
    keywordTaglist.value.reduce((total, item) => total + item.value.length, 0) + val.length;
  const isUnique = !keywordTaglist.value.some(
    (item) => item.value.toLowerCase() === val.toLowerCase(),
  );

  if (totalCharacterCount > characterLimit) {
    errorMessage.value = t("keywords_total_character_limit_invalid_message", {
      num: characterLimit,
    });
    return false;
  }

  if (!isUnique) {
    return false;
  }

  errorMessage.value = "";
  return true;
};

const onInput = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const value = target.value;
  keyStrokeString.value = value;
  const valid = validateInputValue(value);

  if (valid) {
    const characterAndWhiteSpaceRegex = /^([\w\s]+)$/;
    removeCustomOption();

    if (
      unselectedOptions.value != null &&
      unselectedOptions.value.length >= 0 &&
      value.trim() !== "" &&
      characterAndWhiteSpaceRegex.test(value) &&
      !selectedItems.value.some((item) => item.value.toLowerCase() === value.toLowerCase()) &&
      errorMessage.value === ""
    ) {
      dataOptions.value.unshift({
        name: keyStrokeString.value,
        label: `Create "${keyStrokeString.value}"`,
        value: keyStrokeString.value,
      });
    }
  }
};

const removeCustomOption = () => {
  const customTagIndex = dataOptions.value.findIndex((item) => item.label.includes("Create"));
  if (customTagIndex === 0) {
    dataOptions.value.splice(customTagIndex, 1);
  }
};

/* Previously in CV2, this.$refs.picker.filter was exposed and clearing it was done by setting it to "".
 * In CV3, Carbon doesn't defineExpose(filter) so it is not accessible. This method will manually clear the filter
 * by trigerring the clear filter button nested inside, and a subsequent click to close the menu
 */
const clearFilter = () => {
  setTimeout(() => {
    const picker = pickerRef.value;
    if (picker) {
      const comboBoxEl = picker.$el;

      const clearFilterBtn = DOMUtils.elOf(comboBoxEl, ".bx--list-box__selection")[0];
      const listBoxFieldEl = DOMUtils.elOf(comboBoxEl, ".bx--list-box__field")[0];

      clearFilterBtn?.click();
      listBoxFieldEl.click();

      errorMessage.value = "";
    }
  }, 50);
};

// Watchers
watch(
  () => message,
  () => {
    clearFilter();
    removeCustomOption();

    if (message === "save") {
      if (errorMessage.value === "") {
        // Empty function
      }
      handleSave();
    }
  },
);

watch(
  () => tags,
  () => {
    updateKeywordTagList();
  },
);

watch(selectedItems, () => {
  keywordTaglist.value = selectedItems.value;
});

watch(
  () => items,
  () => {
    updateOptions();
  },
);

// Lifecycle
onMounted(() => {
  updateOptions();
  updateKeywordTagList();
});
</script>

<style lang="scss">
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.editable-tag-list-auto-complete {
  label.bx--label {
    font: var(--body-short-01);
    color: var(--text-02);

    margin-bottom: 8px;
  }

  .bx--list-box[data-invalid] {
    border: $divider-02;
  }

  .bx--list-box__wrapper {
    margin-bottom: $spacing-03;
  }

  .bx--list-box {
    .bx--text-input {
      &::placeholder {
        color: $text-03;
      }
    }
  }

  div.cv-tag.bx--tag.bx--tag--filter {
    cursor: initial;
  }

  .bx--tag {
    color: $state-neutral-text;
  }

  .bx--tag__label {
    @include carbon--type-style("label-01", true);
    overflow: visible;
    white-space: normal;
  }
}
</style>
